/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useEffect, useRef, useCallback } from 'react';

import { fg } from '@atlaskit/platform-feature-flags';
import { token } from '@atlaskit/tokens';
import { useThemeObserver } from '@atlaskit/tokens/use-theme-observer';
import { css, jsx } from '@compiled/react';

import { type Color as ColorType } from '../Status';

import Color from './color';

const paletteLegacy: [
	colorValue: ColorType,
	backgroundColor: string,
	borderColor: string,
	iconColor: string,
][] = [
	['neutral', token('color.background.neutral'), token('color.border.bold'), token('color.icon')],
	[
		'purple',
		token('color.background.discovery'),
		token('color.border.discovery'),
		token('color.icon.discovery'),
	],
	[
		'blue',
		token('color.background.information'),
		token('color.border.information'),
		token('color.icon.information'),
	],
	[
		'red',
		token('color.background.danger'),
		token('color.border.danger'),
		token('color.icon.danger'),
	],
	[
		'yellow',
		token('color.background.warning'),
		token('color.border.warning'),
		token('color.icon.warning'),
	],
	[
		'green',
		token('color.background.success'),
		token('color.border.success'),
		token('color.icon.success'),
	],
];

const paletteRefreshed: [
	colorValue: ColorType,
	backgroundColor: string,
	borderColor: string,
	iconColor: string,
][] = [
	[
		'neutral',
		token('color.background.accent.gray.subtler'),
		token('color.border.bold'),
		token('color.icon'),
	],
	[
		'blue',
		token('color.background.information.pressed'),
		token('color.border.information'),
		token('color.icon'),
	],
	[
		'green',
		token('color.background.success.pressed'),
		token('color.border.success'),
		token('color.icon'),
	],
	[
		'yellow',
		token('color.background.warning.pressed'),
		token('color.border.warning'),
		token('color.icon'),
	],
	[
		'red',
		token('color.background.danger.pressed'),
		token('color.border.danger'),
		token('color.icon'),
	],
	[
		'purple',
		token('color.background.discovery.pressed'),
		token('color.border.discovery'),
		token('color.icon'),
	],
];

const paletteTeam26: [
	colorValue: ColorType,
	backgroundColor: string,
	lightBorderColor: string,
	darkBorderColor: string,
	iconColor: string,
][] = [
	['neutral', token('color.background.neutral'), '#CACBCF', '#63666B', token('color.icon')],
	[
		'blue',
		token('color.background.information.subtler'),
		'#8FB8F6',
		'#1558BC',
		token('color.icon'),
	],
	['green', token('color.background.success.subtler'), '#B3DF72', '#4C6B1F', token('color.icon')],
	['yellow', token('color.background.warning.subtler'), '#FBC828', '#9E4C00', token('color.icon')],
	['red', token('color.background.danger.subtler'), '#FD9891', '#AE2E24', token('color.icon')],
	[
		'purple',
		token('color.background.discovery.subtler'),
		'#D8A0F7',
		'#803FA5',
		token('color.icon'),
	],
];

type PaletteEntry = [
	colorValue: ColorType,
	backgroundColor: string,
	borderColor: string,
	iconColor: string,
];

const getPalette = (colorMode?: string): PaletteEntry[] => {
	if (fg('platform-dst-lozenge-tag-badge-visual-uplifts')) {
		const isDark = colorMode === 'dark';
		return paletteTeam26.map(
			([colorValue, backgroundColor, lightBorderColor, darkBorderColor, iconColor]) => [
				colorValue,
				backgroundColor,
				isDark ? darkBorderColor : lightBorderColor,
				iconColor,
			],
		);
	}
	return fg('platform-component-visual-refresh') ? paletteRefreshed : paletteLegacy;
};

// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage, @atlaskit/design-system/no-css-tagged-template-expression -- Ignored via go/DSP-18766
const colorPaletteWrapperStyles = css({
	paddingLeft: '0px',
	marginTop: token('space.100'),
	marginRight: token('space.100'),
	marginBottom: '0px',
	marginLeft: token('space.100'),
	/* Firefox bug fix: https://product-fabric.atlassian.net/browse/ED-1789 */
	display: 'flex',
	flexWrap: 'wrap',
});

interface ColorPaletteProps {
	className?: string;
	cols?: number;
	onClick: (value: ColorType) => void;
	onHover?: (value: ColorType) => void;
	selectedColor?: ColorType;
}

export default ({
	cols = 7,
	onClick,
	selectedColor,
	className,
	onHover,
}: ColorPaletteProps): JSX.Element => {
	const { colorMode } = useThemeObserver();
	const palette = getPalette(colorMode);
	const colorRefs: React.MutableRefObject<HTMLButtonElement[]> = useRef([]);
	useEffect(() => {
		colorRefs.current = colorRefs.current.slice(0, palette.length);
	}, [palette.length]);

	const createKeyDownHandler = useCallback(
		(index: number) => (e: React.KeyboardEvent<HTMLButtonElement>) => {
			let newColorIndex: number | null = null;
			const nextColor = () => (index + 1 > palette.length - 1 ? 0 : index + 1);
			const previousColor = () => (index - 1 < 0 ? palette.length - 1 : index - 1);

			switch (e.key) {
				case 'ArrowRight':
				case 'ArrowDown':
					e.preventDefault();
					newColorIndex = nextColor();
					break;
				case 'ArrowLeft':
				case 'ArrowUp':
					e.preventDefault();
					newColorIndex = previousColor();
					break;
			}
			if (newColorIndex === null) {
				return;
			}
			colorRefs.current[newColorIndex]?.focus();
		},
		[colorRefs, palette.length],
	);

	return (
		<ul
			css={colorPaletteWrapperStyles}
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
			className={className}
			style={{ maxWidth: cols * 32 }}
		>
			{palette.map(([colorValue, backgroundColor, borderColor, iconColor], i) => (
				<Color
					key={colorValue}
					value={colorValue}
					backgroundColor={backgroundColor}
					borderColor={borderColor}
					iconColor={iconColor}
					onClick={onClick}
					onHover={onHover}
					isSelected={colorValue === selectedColor}
					tabIndex={i === 0 ? 0 : -1}
					setRef={(el) => (colorRefs.current[i] = el)}
					onKeyDown={createKeyDownHandler(i)}
				/>
			))}
		</ul>
	);
};
